import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {Nullable} from "../../../bootstrap/common/types";
import {ResizeObserverFactory} from "../../../common/observation";
import {resolve} from "../../../container";
import {PageFeatures} from "../pageFeatures";
import {PageScrollbar} from "../../../common/pageScrollbar";
import {Dictionary} from "../dictionary";
import type {EopEnergyBitStoryViewer} from "./energyBitStoryViewer";
import {trackingTeaserInteract} from "../../../tracking/trackingAttributes";
import {GLOBAL} from "../../../common/globals";

const ASPECT_RATIO_TILE = 16 / 9;
const ASPECT_RATIO_IMAGE = 5 / 4;

export interface CleanableStoryTile {
    cleanUp: () => void;
}

@customElement("eop-energy-bit-story-tile")
export class EopEnergyBitStoryTile extends WithEopSlots(UnLitElement) implements CleanableStoryTile {

    @property({attribute: "text"})
    private text: string;
    @property({attribute: "images-count"})
    private imagesCount: string;
    @property({attribute: "story-viewer-id"})
    private storyViewerId: string;
    @property({attribute: "story-first-image-slot-name"})
    private storyFirstImageSlotName: string;

    @query(".tile-container")
    private tileContainer: HTMLElement;

    private viewer: EopEnergyBitStoryViewer | null;
    private dictionary: Dictionary;
    private resizeObserver: ResizeObserver;

    public constructor(
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private pageFeatures: PageFeatures = resolve(PageFeatures),
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateCSSVariables());
    }

    public render(): TemplateResult {
        return html`
            <div class="tile-container reverse-background">
                <div class="tile-image">
                    ${this.resolveSlot(this.storyFirstImageSlotName)}
                    <div class="image-overlay">
                        ${this.renderBadge()}
                        ${this.renderButton()}
                    </div>
                </div>
                ${this.renderText()}
            </div>
        `;
    }

    private renderBadge(): TemplateResult {
        return html`
            <div class="badge-row">
                <div class="badge">
                    <div class="count">${this.imagesCount}</div>
                </div>
            </div>`;
    }

    private renderButton(): TemplateResult {
        const openLabel = this.dictionary.translate("ENERGY_BIT_STORY_OPEN_LABEL");
        return html`
            <div class="button-row">
                <button type="button"
                        @click=${this.openStory}
                        class="primary"
                        ${trackingTeaserInteract(openLabel)}
                >
                    ${openLabel}
                </button>
            </div> `;
    }

    private renderText(): Nullable<TemplateResult> {
        if (!this.text) {
            return null;
        }
        return html`
            <div class="tile-text">
                <div class="text" data-role="content-sub-headline">${unsafeHTML(this.text)}</div>
            </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.updateCSSVariables();
        this.resizeObserver.observe(this.tileContainer);
    }

    private updateCSSVariables(): void {
        const tileWidth = this.tileContainer.getBoundingClientRect().width;

        this.style.setProperty("--image-tile-height", `${(tileWidth * ASPECT_RATIO_TILE)}px`);
        const textTileHeight = tileWidth * (ASPECT_RATIO_TILE - ASPECT_RATIO_IMAGE);
        const lineHeight = GLOBAL.window().getComputedStyle(this).lineHeight;
        this.style.setProperty("--line-height", lineHeight);
        this.style.setProperty("--lines-to-clamp", `${Math.floor(textTileHeight / Number.parseFloat(lineHeight)) - 1}`);
    }

    private openStory(event: Event): void {
        this.viewer = Array.from(this.pageFeatures.find<EopEnergyBitStoryViewer>("eop-energy-bit-story-viewer"))
            .find(viewer => viewer.getAttribute("story-viewer-id") === this.storyViewerId)
            ?? null;
        if (!this.viewer) {
            return;
        }

        this.viewer.setManagingParent(this);
        this.viewer.setOpener(event.currentTarget as HTMLElement);
        this.viewer.openStory();
        this.pageScrollbar.disablePageScrollability();
    }

    public cleanUp(): void {
        this.pageScrollbar.enablePageScrollability();
    }
}